import { Controller } from 'react-hook-form';
import {
  IconLoader,
  InputSize,
  InputText,
  PermissionEnum,
  Select,
  SelectSize,
  Textarea,
} from '@ui/src';
import InfoRow from '../../common/info-row';
import StepPartBlock from '../../common/step-part-block';
import { NewApplicationStatus } from '../../../../hoc/common/types';
import StepOneAdditionalBlock from './step-one-additional-block';
import { AdditionalInformationTypes } from './types';

import commonStyles from '../../common/styles.module.css';

const AdditionalInformation = ({
  applicationScheme,
  questionnarieControl,
  questionnarieClearErrors,
  questionnarieErrors,
  activePermissions,
  status,
  questionnarieIsLoading,
}: AdditionalInformationTypes) => (
  <StepPartBlock title="Additional Info" zIndex={28}>
    {questionnarieIsLoading && (
      <div className={commonStyles.dictionaryLoader}>
        <IconLoader />
      </div>
    )}
    {!questionnarieIsLoading && (
      <>
        {applicationScheme?.lastThreeYearsAddressHistoryActive && (
          <InfoRow
            title="National Insurance number of directors"
            id="nationalInsuranceNumberOfDirectors"
            lessPadding
          >
            <Controller
              control={questionnarieControl}
              name="nationalInsuranceNumberOfDirectors"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <InputText
                  name="nationalInsuranceNumberOfDirectors"
                  placeholder="Enter count..."
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors([
                      'nationalInsuranceNumberOfDirectors',
                    ]);
                  }}
                  size={InputSize.SMALL}
                  className="borderless"
                  error={
                    questionnarieErrors?.nationalInsuranceNumberOfDirectors
                      ?.message
                  }
                  tabIndex={
                    !activePermissions?.includes(
                      PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                    ) ||
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED
                      ? -1
                      : 0
                  }
                  disabled={
                    !activePermissions?.includes(
                      PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                    ) ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                />
              )}
            />
          </InfoRow>
        )}
        {applicationScheme?.lastThreeYearsAddressHistoryActive && (
          <InfoRow
            title="Last 3 years address history"
            id="lastThreeYearsAddressHistory"
            lessPadding
          >
            <Controller
              control={questionnarieControl}
              name="lastThreeYearsAddressHistory"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Textarea
                  name="lastThreeYearsAddressHistory"
                  placeholder="list the addresses..."
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors(['lastThreeYearsAddressHistory']);
                  }}
                  className={`borderless ${commonStyles.stepOneTextarea} ${commonStyles.pepTextarea}`}
                  error={
                    questionnarieErrors?.lastThreeYearsAddressHistory?.message
                  }
                  tabIndex={
                    !activePermissions?.includes(
                      PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                    ) ||
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED
                      ? -1
                      : 0
                  }
                  disabled={
                    !activePermissions?.includes(
                      PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                    ) ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                />
              )}
            />
          </InfoRow>
        )}
        {applicationScheme?.criminalOfAnyActive && (
          <StepOneAdditionalBlock title="Criminal Proceedings" defaultOpen>
            <>
              <InfoRow
                title="Have you ever been convicted of any criminal offence?"
                id="criminalReceivedACautionForAny"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="criminalReceivedACautionForAny"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="criminalReceivedACautionForAny"
                      placeholder="Select «Yes» or «No»"
                      list={[
                        {
                          name: 'Yes',
                          value: 'yes',
                        },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      value={value as string}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors([
                          'criminalReceivedACautionForAny',
                        ]);
                      }}
                      className="borderless"
                      size={SelectSize.SMALL}
                      error={
                        questionnarieErrors?.criminalReceivedACautionForAny
                          ?.message
                      }
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Have you ever received a caution for any criminal offence?"
                id="criminalConvictedOfAny"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="criminalConvictedOfAny"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="criminalConvictedOfAny"
                      placeholder="Select «Yes» or «No»"
                      list={[
                        {
                          name: 'Yes',
                          value: 'yes',
                        },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      value={value as string}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['criminalConvictedOfAny']);
                      }}
                      className="borderless"
                      size={SelectSize.SMALL}
                      error={
                        questionnarieErrors?.criminalConvictedOfAny?.message
                      }
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Have you ever been arrested or charged with any criminal offence or been involved in a criminal investigation?"
                id="criminalArrestedOrChargedWithOrBeenInvolved"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="criminalArrestedOrChargedWithOrBeenInvolved"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="criminalArrestedOrChargedWithOrBeenInvolved"
                      placeholder="Select «Yes» or «No»"
                      list={[
                        {
                          name: 'Yes',
                          value: 'yes',
                        },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      value={value as string}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors([
                          'criminalArrestedOrChargedWithOrBeenInvolved',
                        ]);
                      }}
                      className="borderless"
                      size={SelectSize.SMALL}
                      error={
                        questionnarieErrors
                          ?.criminalArrestedOrChargedWithOrBeenInvolved?.message
                      }
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Have you ever been subject to any current criminal investigation?"
                id="criminalSubjectToAnyCurrentCriminalInvestigation"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="criminalSubjectToAnyCurrentCriminalInvestigation"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="criminalSubjectToAnyCurrentCriminalInvestigation"
                      placeholder="Select «Yes» or «No»"
                      list={[
                        {
                          name: 'Yes',
                          value: 'yes',
                        },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      value={value as string}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors([
                          'criminalSubjectToAnyCurrentCriminalInvestigation',
                        ]);
                      }}
                      className="borderless"
                      size={SelectSize.SMALL}
                      error={
                        questionnarieErrors
                          ?.criminalSubjectToAnyCurrentCriminalInvestigation
                          ?.message
                      }
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Has any firm where you held a position of influence ever been convicted, charged, or investigated for a criminal offence?"
                id="criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted"
                      placeholder="Select «Yes» or «No»"
                      list={[
                        {
                          name: 'Yes',
                          value: 'yes',
                        },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      value={value as string}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors([
                          'criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted',
                        ]);
                      }}
                      className="borderless"
                      size={SelectSize.SMALL}
                      error={
                        questionnarieErrors
                          ?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted
                          ?.message
                      }
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Has any firm where you held a position of influence ever been ordered to produce documents for or been searched in relation to a criminal investigation?"
                id="criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered"
                      placeholder="Select «Yes» or «No»"
                      list={[
                        {
                          name: 'Yes',
                          value: 'yes',
                        },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      value={value as string}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors([
                          'criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered',
                        ]);
                      }}
                      className="borderless"
                      size={SelectSize.SMALL}
                      error={
                        questionnarieErrors
                          ?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered
                          ?.message
                      }
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Has any firm where you held a position of influence ever been involved in current criminal proceedings or investigations?"
                id="criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved"
                      placeholder="Select «Yes» or «No»"
                      list={[
                        {
                          name: 'Yes',
                          value: 'yes',
                        },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      value={value as string}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors([
                          'criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved',
                        ]);
                      }}
                      className="borderless"
                      size={SelectSize.SMALL}
                      error={
                        questionnarieErrors
                          ?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved
                          ?.message
                      }
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
            </>
          </StepOneAdditionalBlock>
        )}
        {applicationScheme?.civilActive && (
          <StepOneAdditionalBlock title="Civil Proceedings" defaultOpen>
            <>
              <InfoRow
                title="Have you ever been found liable for fraud, negligence, or misconduct by a court?"
                id="civilFoundLiableForFraud"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="civilFoundLiableForFraud"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="civilFoundLiableForFraud"
                      placeholder="Select «Yes» or «No»"
                      list={[
                        {
                          name: 'Yes',
                          value: 'yes',
                        },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      value={value as string}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['civilFoundLiableForFraud']);
                      }}
                      className="borderless"
                      size={SelectSize.SMALL}
                      error={
                        questionnarieErrors?.civilFoundLiableForFraud?.message
                      }
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Have you ever been subject to a judgment debt or award?"
                id="civilSubjectToAJudgmentDebt"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="civilSubjectToAJudgmentDebt"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="civilSubjectToAJudgmentDebt"
                      placeholder="Select «Yes» or «No»"
                      list={[
                        {
                          name: 'Yes',
                          value: 'yes',
                        },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      value={value as string}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors([
                          'civilSubjectToAJudgmentDebt',
                        ]);
                      }}
                      className="borderless"
                      size={SelectSize.SMALL}
                      error={
                        questionnarieErrors?.civilSubjectToAJudgmentDebt
                          ?.message
                      }
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Have you ever been involved in any civil proceedings that resulted in a finding against you?"
                id="civilInvolvedInAnyCivilProceedings"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="civilInvolvedInAnyCivilProceedings"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="civilInvolvedInAnyCivilProceedings"
                      placeholder="Select «Yes» or «No»"
                      list={[
                        {
                          name: 'Yes',
                          value: 'yes',
                        },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      value={value as string}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors([
                          'civilInvolvedInAnyCivilProceedings',
                        ]);
                      }}
                      className="borderless"
                      size={SelectSize.SMALL}
                      error={
                        questionnarieErrors?.civilInvolvedInAnyCivilProceedings
                          ?.message
                      }
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Has any firm where you held a position of influence ever been found liable in civil proceedings for fraud or misconduct?"
                id="civilYouHeldAPositionOfInfluence"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="civilYouHeldAPositionOfInfluence"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="civilYouHeldAPositionOfInfluence"
                      placeholder="Select «Yes» or «No»"
                      list={[
                        {
                          name: 'Yes',
                          value: 'yes',
                        },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      value={value as string}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors([
                          'civilYouHeldAPositionOfInfluence',
                        ]);
                      }}
                      className="borderless"
                      size={SelectSize.SMALL}
                      error={
                        questionnarieErrors?.civilYouHeldAPositionOfInfluence
                          ?.message
                      }
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Have you ever filed for bankruptcy or been judged bankrupt?"
                id="civilFiledForBankruptcy"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="civilFiledForBankruptcy"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="civilFiledForBankruptcy"
                      placeholder="Select «Yes» or «No»"
                      list={[
                        {
                          name: 'Yes',
                          value: 'yes',
                        },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      value={value as string}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['civilFiledForBankruptcy']);
                      }}
                      className="borderless"
                      size={SelectSize.SMALL}
                      error={
                        questionnarieErrors?.civilFiledForBankruptcy?.message
                      }
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Have you ever been subject to a bankruptcy restriction order or made any arrangements with creditors?"
                id="civilSubjectToABankruptcy"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="civilSubjectToABankruptcy"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="civilSubjectToABankruptcy"
                      placeholder="Select «Yes» or «No»"
                      list={[
                        {
                          name: 'Yes',
                          value: 'yes',
                        },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      value={value as string}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['civilSubjectToABankruptcy']);
                      }}
                      className="borderless"
                      size={SelectSize.SMALL}
                      error={
                        questionnarieErrors?.civilSubjectToABankruptcy?.message
                      }
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
            </>
          </StepOneAdditionalBlock>
        )}
        {applicationScheme?.businessAndEmploymentActive && (
          <StepOneAdditionalBlock
            title="Business and Employment Matters"
            defaultOpen
          >
            <>
              <InfoRow
                title="Have you ever been dismissed or asked to resign from a position?"
                id="businessAndEmploymentDismissedOrAskedTo"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="businessAndEmploymentDismissedOrAskedTo"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="businessAndEmploymentDismissedOrAskedTo"
                      placeholder="Select «Yes» or «No»"
                      list={[
                        {
                          name: 'Yes',
                          value: 'yes',
                        },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      value={value as string}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors([
                          'businessAndEmploymentDismissedOrAskedTo',
                        ]);
                      }}
                      className="borderless"
                      size={SelectSize.SMALL}
                      error={
                        questionnarieErrors
                          ?.businessAndEmploymentDismissedOrAskedTo?.message
                      }
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Have you ever been disqualified from acting as a director?"
                id="businessAndEmploymentDisqualifiedFromActingAsADirector"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="businessAndEmploymentDisqualifiedFromActingAsADirector"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="businessAndEmploymentDisqualifiedFromActingAsADirector"
                      placeholder="Select «Yes» or «No»"
                      list={[
                        {
                          name: 'Yes',
                          value: 'yes',
                        },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      value={value as string}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors([
                          'businessAndEmploymentDisqualifiedFromActingAsADirector',
                        ]);
                      }}
                      className="borderless"
                      size={SelectSize.SMALL}
                      error={
                        questionnarieErrors
                          ?.businessAndEmploymentDisqualifiedFromActingAsADirector
                          ?.message
                      }
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Have you ever been the subject of any disciplinary proceedings or investigations?"
                id="businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings"
                      placeholder="Select «Yes» or «No»"
                      list={[
                        {
                          name: 'Yes',
                          value: 'yes',
                        },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      value={value as string}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors([
                          'businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings',
                        ]);
                      }}
                      className="borderless"
                      size={SelectSize.SMALL}
                      error={
                        questionnarieErrors
                          ?.businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings
                          ?.message
                      }
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
            </>
          </StepOneAdditionalBlock>
        )}
        {applicationScheme?.regulatoryActive && (
          <StepOneAdditionalBlock title="Regulatory Matters" defaultOpen>
            <>
              <InfoRow
                title="Have you ever had an authorization from a regulatory body refused, revoked, or terminated?"
                id="regulatoryAnAuthorizationFromARegulatoryBodyRefused"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="regulatoryAnAuthorizationFromARegulatoryBodyRefused"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="regulatoryAnAuthorizationFromARegulatoryBodyRefused"
                      placeholder="Select «Yes» or «No»"
                      list={[
                        {
                          name: 'Yes',
                          value: 'yes',
                        },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      value={value as string}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors([
                          'regulatoryAnAuthorizationFromARegulatoryBodyRefused',
                        ]);
                      }}
                      className="borderless"
                      size={SelectSize.SMALL}
                      error={
                        questionnarieErrors
                          ?.regulatoryAnAuthorizationFromARegulatoryBodyRefused
                          ?.message
                      }
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Have you ever been censured, disciplined, or investigated by a regulatory body?"
                id="regulatoryCensured"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="regulatoryCensured"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="regulatoryCensured"
                      placeholder="Select «Yes» or «No»"
                      list={[
                        {
                          name: 'Yes',
                          value: 'yes',
                        },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      value={value as string}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['regulatoryCensured']);
                      }}
                      className="borderless"
                      size={SelectSize.SMALL}
                      error={questionnarieErrors?.regulatoryCensured?.message}
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Have you ever been involved in any activities requiring authorization that you did not have?"
                id="regulatoryInvolvedInAnyActivitiesRequiringAuthorization"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="regulatoryInvolvedInAnyActivitiesRequiringAuthorization"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="regulatoryInvolvedInAnyActivitiesRequiringAuthorization"
                      placeholder="Select «Yes» or «No»"
                      list={[
                        {
                          name: 'Yes',
                          value: 'yes',
                        },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      value={value as string}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors([
                          'regulatoryInvolvedInAnyActivitiesRequiringAuthorization',
                        ]);
                      }}
                      className="borderless"
                      size={SelectSize.SMALL}
                      error={
                        questionnarieErrors
                          ?.regulatoryInvolvedInAnyActivitiesRequiringAuthorization
                          ?.message
                      }
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Have you ever been subject to any disqualification or prohibition orders under financial regulations?"
                id="regulatorySubjectToAnyDisqualification"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="regulatorySubjectToAnyDisqualification"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="regulatorySubjectToAnyDisqualification"
                      placeholder="Select «Yes» or «No»"
                      list={[
                        {
                          name: 'Yes',
                          value: 'yes',
                        },
                        {
                          name: 'No',
                          value: 'no',
                        },
                      ]}
                      value={value as string}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors([
                          'regulatorySubjectToAnyDisqualification',
                        ]);
                      }}
                      className="borderless"
                      size={SelectSize.SMALL}
                      error={
                        questionnarieErrors
                          ?.regulatorySubjectToAnyDisqualification?.message
                      }
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
            </>
          </StepOneAdditionalBlock>
        )}
      </>
    )}
  </StepPartBlock>
);

export default AdditionalInformation;
